/**
 * Admin impersonation helpers — read session state and restore the original
 * admin account after "Exit view". Mirrors sidebar `handleSearchReset` storage steps.
 */

export const IMPERSONATION_ENTER_KEY = 'studyak_impersonation_enter';
export const IMPERSONATION_TRANSITION_KEY = 'studyak_impersonation_transition';
export const IMPERSONATION_TRANSITION_EVENT = 'studyak:impersonation-transition';
export const IMPERSONATION_HOME_PATH = '/Portal/Instructor';
/**
 * Minimum time the pre-reload curtain stays up before navigating.
 * Matches PENDING_CLOSE_MS (550) + brief avatar/spinner beat in ImpersonationPendingOverlay.
 */
export const IMPERSONATION_MIN_PRELOAD_MS = 1200;

/** Lets the enter curtain play before reload even when the plan API responds instantly. */
export function waitForImpersonationPreload(
  minMs = IMPERSONATION_MIN_PRELOAD_MS,
) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, minMs);
  });
}

/** Fires overlay on the current page before reload — keeps the switch visually continuous. */
export function beginImpersonationTransition(displayName, email) {
  sessionStorage.setItem(
    IMPERSONATION_TRANSITION_KEY,
    JSON.stringify({ displayName, email }),
  );
  window.dispatchEvent(new CustomEvent(IMPERSONATION_TRANSITION_EVENT));
}

/** @returns {{ displayName?: string, email?: string } | null} */
export function consumeImpersonationTransitionMeta() {
  const raw = sessionStorage.getItem(IMPERSONATION_TRANSITION_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(IMPERSONATION_TRANSITION_KEY);
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function hasImpersonationTransitionPending() {
  return sessionStorage.getItem(IMPERSONATION_TRANSITION_KEY) != null;
}

/** Clears a stuck pre-reload transition overlay (e.g. failed or timed-out impersonation). */
export function clearImpersonationTransition() {
  sessionStorage.removeItem(IMPERSONATION_TRANSITION_KEY);
}

/** Set before reload when admin picks a user — triggers frame enter animation once. */
export function markImpersonationEnter() {
  sessionStorage.setItem(IMPERSONATION_ENTER_KEY, '1');
}

/** @returns {boolean} True once after impersonation start; clears the flag. */
export function consumeImpersonationEnterAnimation() {
  const shouldAnimate = sessionStorage.getItem(IMPERSONATION_ENTER_KEY) === '1';
  if (shouldAnimate) {
    sessionStorage.removeItem(IMPERSONATION_ENTER_KEY);
  }
  return shouldAnimate;
}

/** @returns {boolean} True when the portal is viewing as another user. */
export function isImpersonating() {
  const loggedInEmail = localStorage.getItem('loggedInEmail');
  if (loggedInEmail) {
    return true;
  }

  const userId = localStorage.getItem('userId');
  const userId1 = localStorage.getItem('userId1');
  return Boolean(userId1 && userId && userId !== userId1);
}

/** @returns {string} Email shown in impersonation UI, or empty when unavailable. */
export function getImpersonatedEmail() {
  return localStorage.getItem('loggedInEmail') || localStorage.getItem('email') || '';
}

/** @returns {string} Full email for "View as …" labels. */
export function getViewAsLabel() {
  return getImpersonatedEmail();
}

/** Builds a greeting-safe display name from a user search row. */
export function buildUserDisplayName(user) {
  if (!user) return 'User';
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  const email = user.email || '';
  const localPart = email.split('@')[0];
  return localPart || 'User';
}

/** Root admin email stashed before impersonation — used for sidebar sign-out chrome. */
export function getRootAccountEmail() {
  return localStorage.getItem('email1') || localStorage.getItem('email') || '';
}

/** Root admin display name — sidebar footer while viewing as another user. */
export function getRootAccountDisplayName() {
  const adminUsername = localStorage.getItem('username1');
  if (adminUsername) return adminUsername;

  const email = getRootAccountEmail();
  if (email) return email.split('@')[0] || 'User';

  return localStorage.getItem('username') || localStorage.getItem('name') || 'User';
}

/** Removes impersonation stash keys without restoring the admin session. */
export function clearImpersonationStorage() {
  localStorage.removeItem('loggedInEmail');
  localStorage.removeItem('email1');
  localStorage.removeItem('username1');
  localStorage.removeItem('packageDetails1');
  localStorage.removeItem('price1');
  clearIdentityEntitlementCache();
}

/** Display name for portal chrome (dashboard greeting, sidebar profile). */
export function getPortalDisplayName() {
  if (isImpersonating()) {
    const username = localStorage.getItem('username') || '';
    const adminUsername = localStorage.getItem('username1');
    if (username && adminUsername && username !== adminUsername) {
      return username;
    }
    const email = getImpersonatedEmail();
    if (email) return email.split('@')[0] || 'User';
    return username || 'User';
  }

  return localStorage.getItem('username') || localStorage.getItem('name') || '';
}

/**
 * Clears cached role/plan/meter keys so the next identity cannot inherit the
 * prior viewer's access while entitlements reload after enter or exit view.
 */
export function clearIdentityEntitlementCache() {
  localStorage.removeItem('instructorRole');
  localStorage.removeItem('studyak_plan_tier');
  localStorage.removeItem('studyak_package_limits');
}

/** Preserves the admin display name before impersonation. */
export function stashAdminUsername() {
  localStorage.setItem('username1', localStorage.getItem('username') || '');
}

/** Restores the admin display name after exit view. */
export function restoreAdminUsername() {
  const adminUsername = localStorage.getItem('username1');
  if (adminUsername !== null) {
    localStorage.setItem('username', adminUsername);
    localStorage.removeItem('username1');
  }
}

/** @returns {string} Two-letter initials derived from the impersonated email. */
export function getImpersonatedInitials() {
  const email = getImpersonatedEmail();
  if (!email) {
    return '?';
  }
  const localPart = email.split('@')[0] || email;
  const segments = localPart.split(/[._-]+/).filter(Boolean);
  if (segments.length >= 2) {
    return `${segments[0][0]}${segments[1][0]}`.toUpperCase();
  }
  return localPart.slice(0, 2).toUpperCase();
}

/** Restores admin localStorage keys without reloading the page. */
export function restoreImpersonationSession() {
  clearIdentityEntitlementCache();

  const userId1 = localStorage.getItem('userId1');
  localStorage.setItem('userId', userId1);
  localStorage.setItem('loggedInEmail', '');

  const email = localStorage.getItem('email1');
  localStorage.setItem('email', email);

  const packageDetails = localStorage.getItem('packageDetails1');
  const price = localStorage.getItem('price1');
  localStorage.setItem('packageDetails', packageDetails);
  localStorage.setItem('price', price);

  restoreAdminUsername();
}

/** Navigates to dashboard — resets page state after an identity switch. */
export function navigateToImpersonationHome() {
  window.location.assign(IMPERSONATION_HOME_PATH);
}

/** Restores admin session and returns to dashboard — call after exit animation completes. */
export function finishExitImpersonation() {
  restoreImpersonationSession();
  navigateToImpersonationHome();
}

/** Restores admin localStorage keys and reloads immediately. */
export function exitImpersonationView() {
  finishExitImpersonation();
}
